/**
 * The native-grain rate grid (CLE-2470) — Ani's v0 steps 2 and 3.
 *
 * One row per priced line, in the contract's own vocabulary (revenue codes, DRGs, service lines),
 * grouped by service line, with methodology / rate / bundled / carve-out and the agent's confidence
 * side by side. This is the surface for reading a rate sheet: a 16-line outpatient grid is 16 rows you
 * can scan, not 16 stacked cards you have to page through.
 *
 * **The grid does not edit.** Expanding a row hands off to `ProposedModelingForm`, which stays the one
 * place a value is changed and re-validated against its Zod schema. Duplicating field editing into
 * table cells would mean two code paths writing the same construct with different validation, which is
 * exactly the drift the typed schema was introduced to end. So: grid to read and triage, form to fix.
 */

import { Tag } from "primereact/tag";
import React, { useState } from "react";
import styled from "styled-components";

import { IDraftRecommendation } from "../../../store/queries/contractModelDrafts";
import { confidenceSeverity } from "../utils";
import {
  IRateGridGroup,
  IRateGridRow,
  gridSummary,
  toGridGroups,
} from "./rateGridRows";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const GroupCard = styled.div`
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  overflow: hidden;
  /* A solid card surface, so the table reads as a component and doesn't dissolve into the page's grey. */
  background: var(--surface-0, #fff);
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.9rem;
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-border);
  font-weight: 600;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th,
  td {
    padding: 0.5rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid var(--surface-border);
    vertical-align: middle;
    white-space: nowrap;
  }

  th {
    font-weight: 600;
    color: var(--text-color-secondary);
    font-size: 0.8rem;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr.expanded > td {
    background: var(--surface-50);
  }

  /* The expanded editor spans the full row and must lay out normally (its own dropdowns and inputs), not
     as one nowrap table cell — and it carries no cell padding of its own; DetailPanel supplies it. */
  td.detail {
    white-space: normal;
    padding: 0;
  }
`;

/* Codes are the one column allowed to be long; it wraps instead of stretching the table. */
const Codes = styled.span`
  font-family: var(--font-family-monospace, monospace);
  font-size: 0.8rem;
  white-space: normal;
  word-break: break-word;
  display: inline-block;
  max-width: 22rem;
`;

const Muted = styled.span`
  color: var(--text-color-secondary);
`;

/**
 * The expanded row's editor, rendered IN PLACE as a full-width row directly beneath the row it belongs
 * to (a `<td colSpan>` drawer), so expanding the first of three lines opens it under THAT line, not below
 * the whole table.
 *
 * It used to live below the table to dodge the horizontal scroller: inside `overflow-x: auto` the form's
 * dropdowns and inputs were clipped at the right edge. The grid no longer has that scroller — the only
 * long columns (Codes, Description) wrap on their own, so nothing can push the page sideways — which frees
 * the editor to sit in place without being clipped.
 */
const DetailPanel = styled.div`
  border-top: 1px solid var(--surface-border);
  background: var(--surface-50);
  padding: 1rem;
`;

/* Description is the one label column allowed to wrap, so dropping the horizontal scroller can never let a
   long line description push the page sideways (Codes already wraps via its own span). */
const Desc = styled.span`
  white-space: normal;
  word-break: break-word;
  display: inline-block;
  max-width: 22rem;
`;

const DetailHeader = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-color-secondary);
  margin-bottom: 0.5rem;
`;

const ClickableRow = styled.tr`
  cursor: pointer;
  &:hover td {
    background: var(--surface-50);
  }
  &.expanded td {
    background: var(--surface-100);
  }
`;

const CODE_KIND_LABEL: Record<string, string> = {
  revenue: "Rev",
  drg: "DRG",
  cpt: "CPT",
  none: "",
};

/** Long code lists are truncated with an exact remainder — "and 12 more" is a checkable claim. */
const renderCodes = (row: IRateGridRow): React.ReactNode => {
  if (row.codes.length === 0) return <Muted>—</Muted>;
  const shown = row.codes.slice(0, 8);
  const rest = row.codes.length - shown.length;
  const prefix = CODE_KIND_LABEL[row.codeKind];
  return (
    <Codes>
      {prefix ? `${prefix} ` : ""}
      {shown.join(", ")}
      {rest > 0 ? <Muted> and {rest} more</Muted> : null}
    </Codes>
  );
};

const statusTag = (row: IRateGridRow): React.ReactNode => {
  if (row.needsInput) return <Tag severity="danger" value="Needs input" />;
  if (row.status === "accepted")
    return <Tag severity="success" value="Accepted" />;
  // A rejected row is deliberately the quietest thing in the column — it is settled, not a problem.
  if (row.status === "rejected") return <Muted>Rejected</Muted>;
  if (row.status === "overridden")
    return <Tag severity="warning" value="Overridden" />;
  return <Tag severity="info" value="Pending" />;
};

interface IRateGridProps {
  recs: IDraftRecommendation[];
  /** Renders the full editable card for a row — the wizard passes its existing RecommendationCard. */
  renderDetail: (rec: IDraftRecommendation) => React.ReactNode;
  /** Scrolled-to / auto-expanded row, so a deep link into a recommendation still lands. */
  focusRecId?: string | null;
}

function RateGrid({ recs, renderDetail, focusRecId }: IRateGridProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const byId = new Map(recs.map((r) => [r.id, r]));
  const groups: IRateGridGroup[] = toGridGroups(recs);
  const summary = gridSummary(groups);

  const isExpanded = (id: string): boolean =>
    expanded[id] ?? (focusRecId != null && focusRecId === id);

  const toggle = (id: string): void =>
    setExpanded((prev) => ({ ...prev, [id]: !isExpanded(id) }));

  if (recs.length === 0) return null;

  return (
    <Wrap>
      <div className="flex align-items-center gap-2 flex-wrap">
        <Tag
          severity="info"
          value={`${summary.lines} rate ${
            summary.lines === 1 ? "line" : "lines"
          }`}
        />
        {summary.carveOuts > 0 ? (
          <Tag severity="warning" value={`${summary.carveOuts} carve-outs`} />
        ) : null}
        {summary.bundled > 0 ? (
          <Tag severity="info" value={`${summary.bundled} bundled`} />
        ) : null}
      </div>

      {groups.map((group) => (
        <GroupCard key={group.serviceLine}>
          <GroupHeader>
            <i className="pi pi-tag" />
            <span>{group.serviceLine}</span>
            <Muted className="ml-auto text-sm">
              {group.rows.length} {group.rows.length === 1 ? "line" : "lines"}
            </Muted>
          </GroupHeader>
          <Table>
            <thead>
              <tr>
                <th style={{ width: "2.5rem" }} aria-label="Expand" />
                <th>Description</th>
                <th>Codes</th>
                <th>Methodology</th>
                <th>Rate</th>
                <th>Bundled</th>
                <th>Carve-out</th>
                <th>AI</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {group.rows.map((row) => {
                const open = isExpanded(row.id);
                const rec = byId.get(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <ClickableRow
                      className={open ? "expanded" : undefined}
                      onClick={() => toggle(row.id)}
                    >
                      <td>
                        <i
                          className={
                            open ? "pi pi-chevron-down" : "pi pi-chevron-right"
                          }
                          aria-label={open ? "Collapse" : "Expand"}
                        />
                      </td>
                      <td>
                        <Desc>{row.description}</Desc>
                      </td>
                      <td>{renderCodes(row)}</td>
                      <td>{row.methodology}</td>
                      <td>{row.rate || <Muted>—</Muted>}</td>
                      <td>
                        {row.bundled ? (
                          <i className="pi pi-check" aria-label="Bundled" />
                        ) : (
                          <Muted>—</Muted>
                        )}
                      </td>
                      <td>
                        {row.carveOut ? (
                          <i className="pi pi-check" aria-label="Carve-out" />
                        ) : (
                          <Muted>—</Muted>
                        )}
                      </td>
                      <td>
                        {row.confidence ? (
                          <Tag
                            severity={confidenceSeverity(row.confidence)}
                            value={row.confidence}
                          />
                        ) : (
                          <Muted>—</Muted>
                        )}
                      </td>
                      <td>{statusTag(row)}</td>
                    </ClickableRow>
                    {/* The editor opens IN PLACE, as a full-width drawer directly under its own row. */}
                    {open && rec ? (
                      <tr className="expanded">
                        <td className="detail" colSpan={9}>
                          <DetailPanel>
                            <DetailHeader>{row.description}</DetailHeader>
                            {renderDetail(rec)}
                          </DetailPanel>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </GroupCard>
      ))}
    </Wrap>
  );
}

export default RateGrid;
